import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Typography,
  Grid,
  Box,
  Button,
  CircularProgress,
  Switch,
  withStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@material-ui/core";
import { Check } from "@material-ui/icons";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import { useStyles } from './StatusSelectionStyles';
import { updateProjectStatusesAPI } from "../stepperAPI";
import jwt_decode from "jwt-decode";

// Add global styles for ios-switch and checked-switch classes
const globalStyles = `
  .ios-switch > * {
    height: 22px !important;
  }
  .ios-switch .MuiSwitch-switchBase .MuiSwitch-track {
    background-color: #c6c6c6 !important;
  }
  .ios-switch .MuiSwitch-switchBase {
    left: 2px;
    top: -1px;
  }
  .ios-switch .MuiSwitch-switchBase.Mui-checked {
    left: auto;
    right: 18px;
  }
  .ios-switch .MuiSwitch-switchBase.Mui-checked .MuiSwitch-track {
    background-color: #1890ff !important;
  }
  .checked-switch {
    position: absolute;
    width: 16px !important;
    left: 12px;
    top: -3px;
    color: white;
  }
`;

// Inject global styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = globalStyles;
  if (!document.head.querySelector('style[data-status-selection-styles]')) {
    styleSheet.setAttribute('data-status-selection-styles', 'true');
    document.head.appendChild(styleSheet);
  }
}

// Custom styled switch with checkmark support
const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 44,
    height: 22,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "& .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#1890ff !important",
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#1890ff !important",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 18,
    height: 18,
  },
  track: {
    borderRadius: 26 / 2,
    backgroundColor: "#c6c6c6",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(Switch);

// Static data
const PROJECT_STATUSES = [
  "Created",
  "Initiated",
  "In-Progress",
  "Completed",
  "On-Hold",
  "Cancelled",
  "Closed",
];

const PO_STATUSES = [
  "Created",
  "New PO",
  "Under Confirmation",
  "Confirmed",
  "Changes Under Review",
  "Changed",
  "Delivery Complete",
];

const CAMPAIGN_STATUSES = [
  "Ongoing",
  "Complete",
  "Delayed",
  "Pending",
  "Inactive",
];

const StatusSelection = ({
  formData,
  updateStepData,
  classes: parentClasses,
  handleNext,
  handleBack,
  activeStep,
  stepsLength,
  markApiCallSuccessful,
  hideStepActions = false, // Hide navigation buttons when used in review page
  readOnly = false, // Read-only mode for review page
}) => {
  const classes = useStyles();

  // API base URL
  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");

    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, []);

  // Initialize step data - using API structure
  const [stepData, setStepData] = useState(() => {
    const saved = formData?.fgStatuses || formData?.statusSelection || {};
    return {
      projectStatuses: saved.projectStatuses || [],
      campaignStatuses: saved.campaignStatuses || [],
      changeControlStatuses: saved.changeControlStatuses || [],
      deviationStatuses: saved.deviationStatuses || [],
      inProcessTestStatuses: saved.inProcessTestStatuses || [],
      productReleaseTestStatuses: saved.productReleaseTestStatuses || [],
    };
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiError, setApiError] = useState(null);
  const dropdownAPIsLoadedRef = useRef(false);

  // Fetch PD Statuses using real API (all 6 status types)
  const fetchPDStatus = useCallback(async () => {
    if (dropdownAPIsLoadedRef.current || !apiBaseUrl) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch PD Statuses - use false parameter like fetchFGStatuses in commercial
      const pdStatusUrl = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchPDStatuses/false`;
      const pdResponse = await axios.get(pdStatusUrl);

      if (pdResponse?.data?.status === true || pdResponse?.data?.status === "true") {
        const pdData = pdResponse?.data?.data;

        // The master response gives the LIST; the selection comes from whatever
        // is already in form state, which on Edit Project is the project's own
        // saved selection (ProductDevelopmentStepper resolves seed-then-override
        // before this screen renders). This used to be a hardcoded
        // `isAddFlow = true` reading status.activated straight off the master,
        // which overwrote that resolved selection with master defaults on mount
        // — so a status the user had switched off reappeared switched on.
        // With nothing in form state (the add flow) the master flag still
        // applies, exactly as before.
        //
        // BMS / CMO stay independent per FSD §5: sponsorSelection -> BMS,
        // cmoSelection -> CMO, surfaced by the stepper as
        // activated_bms / activated_cmo. `??` so an explicit false is kept
        // rather than falling through to the collapsed `activated`.
        const savedFg = formData?.fgStatuses || formData?.statusSelection || {};
        const rowFor = (status, typeKey, hasBmsCmo) => {
          const id = status.statusID ?? status.status_id;
          const name = status.statusName || status.status_name || "";
          const saved = (savedFg[typeKey] || []).find(
            (r) => (r?.status_id ?? r?.statusID) === id
          );
          const src = saved || status;
          if (hasBmsCmo) {
            return {
              status_id: id,
              status_name: name,
              valueBms: (src.valueBms ?? src.activated_bms ?? src.activated) === true,
              valueCmo: (src.valueCmo ?? src.activated_cmo ?? src.activated) === true,
            };
          }
          return {
            status_id: id,
            status_name: name,
            value: (src.value ?? src.activated) === true,
          };
        };

        // Process all 6 status types from PD Statuses
        const projectStatuses = (pdData?.projectStatuses || []).map((s) =>
          rowFor(s, "projectStatuses", false)
        );
        const campaignStatuses = (pdData?.campaignStatuses || []).map((s) =>
          rowFor(s, "campaignStatuses", false)
        );
        const changeControlStatuses = (pdData?.changeControlStatuses || []).map((s) =>
          rowFor(s, "changeControlStatuses", true)
        );
        const deviationStatuses = (pdData?.deviationStatuses || []).map((s) =>
          rowFor(s, "deviationStatuses", true)
        );
        const inProcessTestStatuses = (pdData?.inProcessTestStatuses || []).map((s) =>
          rowFor(s, "inProcessTestStatuses", true)
        );
        const productReleaseTestStatuses = (pdData?.productReleaseTestStatuses || []).map((s) =>
          rowFor(s, "productReleaseTestStatuses", true)
        );
        
        // Combine all statuses
        const processedData = {
          projectStatuses,
          campaignStatuses,
          changeControlStatuses,
          deviationStatuses,
          inProcessTestStatuses,
          productReleaseTestStatuses,
        };

        setStepData(processedData);
        if (typeof updateStepData === "function") {
          updateStepData("fgStatuses", processedData);
        }
        dropdownAPIsLoadedRef.current = true;
      } else {
        setError("Failed to load statuses");
      }
    } catch (error) {
      console.error("Error fetching PD statuses:", error);
      setError("Failed to load statuses. Please try again.");
    } finally {
      setIsLoading(false);
    }
    // formData is read for the saved-selection overlay above. The
    // dropdownAPIsLoadedRef guard means a new identity here does not refetch,
    // it just avoids capturing a stale formData.
  }, [apiBaseUrl, updateStepData, formData]);

  // Fetch statuses on mount
  useEffect(() => {
    fetchPDStatus();
  }, [fetchPDStatus]);

  // Re-sync the local toggles from form state when this section leaves edit
  // mode. Discard restores the pre-edit values into form state, but this
  // component holds its own copy of every toggle, so without this the
  // discarded edits stayed on screen and looked as though they had been kept.
  const prevReadOnlyRef = useRef(readOnly);
  useEffect(() => {
    const wasEditing = prevReadOnlyRef.current === false;
    prevReadOnlyRef.current = readOnly;
    if (!wasEditing || readOnly !== true) return;

    const savedFg = formData?.fgStatuses || formData?.statusSelection || {};
    const resync = (typeKey, hasBmsCmo) =>
      (savedFg[typeKey] || []).map((r) => {
        const base = {
          status_id: r.status_id ?? r.statusID,
          status_name: r.status_name ?? r.statusName ?? "",
        };
        return hasBmsCmo
          ? {
              ...base,
              valueBms: (r.valueBms ?? r.activated_bms ?? r.activated) === true,
              valueCmo: (r.valueCmo ?? r.activated_cmo ?? r.activated) === true,
            }
          : { ...base, value: (r.value ?? r.activated) === true };
      });

    setStepData((prev) => {
      const next = {
        projectStatuses: resync("projectStatuses", false),
        campaignStatuses: resync("campaignStatuses", false),
        changeControlStatuses: resync("changeControlStatuses", true),
        deviationStatuses: resync("deviationStatuses", true),
        inProcessTestStatuses: resync("inProcessTestStatuses", true),
        productReleaseTestStatuses: resync("productReleaseTestStatuses", true),
      };
      // Nothing to restore from (e.g. form state not populated) — keep what is
      // on screen rather than blanking every table.
      const isEmpty = Object.values(next).every((list) => list.length === 0);
      if (isEmpty) return prev;
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, [readOnly, formData]);

  // Handle toggle changes for status items - matching API structure
  const handleToggleStatus = useCallback(
    (category, statusId, checked, selectionType = "value") => {
      setStepData((prevData) => {
        const updatedData = JSON.parse(JSON.stringify(prevData));
        
        if (category === "projectstatus" && updatedData.projectStatuses) {
          const statusIndex = updatedData.projectStatuses.findIndex(
            (item) => item && item.status_id === statusId
          );
          if (statusIndex !== -1) {
            updatedData.projectStatuses[statusIndex].value = checked;
          }
        } else if (category === "campaignstatus" && updatedData.campaignStatuses) {
          const statusIndex = updatedData.campaignStatuses.findIndex(
            (item) => item && item.status_id === statusId
          );
          if (statusIndex !== -1) {
            updatedData.campaignStatuses[statusIndex].value = checked;
          }
        } else if (category === "changecontrolstatus" && updatedData.changeControlStatuses) {
          const statusIndex = updatedData.changeControlStatuses.findIndex(
            (item) => item && item.status_id === statusId
          );
          if (statusIndex !== -1) {
            if (selectionType === "bms") {
              updatedData.changeControlStatuses[statusIndex].valueBms = checked;
            } else if (selectionType === "cmo") {
              updatedData.changeControlStatuses[statusIndex].valueCmo = checked;
            }
          }
        } else if (category === "deviationstatus" && updatedData.deviationStatuses) {
          const statusIndex = updatedData.deviationStatuses.findIndex(
            (item) => item && item.status_id === statusId
          );
          if (statusIndex !== -1) {
            if (selectionType === "bms") {
              updatedData.deviationStatuses[statusIndex].valueBms = checked;
            } else if (selectionType === "cmo") {
              updatedData.deviationStatuses[statusIndex].valueCmo = checked;
            }
          }
        } else if (category === "inprocessteststatus" && updatedData.inProcessTestStatuses) {
          const statusIndex = updatedData.inProcessTestStatuses.findIndex(
            (item) => item && item.status_id === statusId
          );
          if (statusIndex !== -1) {
            if (selectionType === "bms") {
              updatedData.inProcessTestStatuses[statusIndex].valueBms = checked;
            } else if (selectionType === "cmo") {
              updatedData.inProcessTestStatuses[statusIndex].valueCmo = checked;
            }
          }
        } else if (category === "productreleaseteststatus" && updatedData.productReleaseTestStatuses) {
          const statusIndex = updatedData.productReleaseTestStatuses.findIndex(
            (item) => item && item.status_id === statusId
          );
          if (statusIndex !== -1) {
            if (selectionType === "bms") {
              updatedData.productReleaseTestStatuses[statusIndex].valueBms = checked;
            } else if (selectionType === "cmo") {
              updatedData.productReleaseTestStatuses[statusIndex].valueCmo = checked;
            }
          }
        }

        if (typeof updateStepData === "function") {
          updateStepData("fgStatuses", updatedData);
        }
        return updatedData;
      });
    },
    [updateStepData]
  );

  // Helper to get cdmoOrgID from formData (from ProductDetails step)
  const getCdmoOrgID = useCallback(() => {
    // Try to get from formData or sessionStorage
    try {
      const savedFormData = sessionStorage.getItem("productDevelopmentFormData");
      if (savedFormData) {
        const parsed = JSON.parse(savedFormData);
        const cdmoOrgID = parsed.productDetails?.cdmoOrgID || null;
        console.log("🔍 getCdmoOrgID - Retrieved from sessionStorage:", {
          cdmoOrgID,
          productDetails: parsed.productDetails
        });
        return cdmoOrgID;
      }
    } catch (error) {
      console.error("Error getting cdmoOrgID:", error);
    }
    console.log("⚠️ getCdmoOrgID - No data found in sessionStorage");
    return null;
  }, []);

  // Helper to get collabrixProjectID from formData
  const getCollabrixProjectID = useCallback(() => {
    try {
      const savedFormData = sessionStorage.getItem("productDevelopmentFormData");
      if (savedFormData) {
        const parsed = JSON.parse(savedFormData);
        return parsed.productDetails?.collabrixProjectID || null;
      }
    } catch (error) {
      console.error("Error getting collabrixProjectID:", error);
    }
    return null;
  }, []);

  // Helper to map statuses to API format
  const mapStatusesToAPI = useCallback((statuses, statusObjectType, hasBmsCmo = false) => {
    if (!Array.isArray(statuses)) return [];
    
    // For project and campaign, only send sponsorSelection (single selection)
    // For others (changeControl, deviation, inProcessTest, productReleaseTest), send both sponsorSelection and cmoSelection
    const isSingleSelection = statusObjectType === "project" || statusObjectType === "campaign";
    
    return statuses
      .filter((status) => {
        // Only include statuses that are selected
        if (hasBmsCmo) {
          return status.valueBms === true || status.valueCmo === true;
        } else {
          return status.value === true;
        }
      })
      .map((status) => {
        const mappedStatus = {
          statusID: status.status_id || status.statusID,
          statusName: status.status_name || status.statusName || "",
          sponsorSelection: hasBmsCmo ? (status.valueBms === true) : (status.value === true),
          statusObjectType: statusObjectType,
        };
        
        // Only include cmoSelection for status types that need both selections
        if (!isSingleSelection) {
          mappedStatus.cmoSelection = hasBmsCmo ? (status.valueCmo === true) : (status.value === true);
        }
        
        return mappedStatus;
      });
  }, []);

  // Handle save and next
  const handleSaveAndNext = useCallback(async () => {
    setIsSaving(true);
    setApiError(null); // Clear previous errors
    
    // Validation: Check that at least one status is selected for each status type
    const validationErrors = [];
    
    const hasProjectStatus = stepData.projectStatuses?.some(status => status.value === true);
    if (!hasProjectStatus) {
      validationErrors.push("At least one Project Status must be selected");
    }
    
    const hasCampaignStatus = stepData.campaignStatuses?.some(status => status.value === true);
    if (!hasCampaignStatus) {
      validationErrors.push("At least one Campaign Status must be selected");
    }
    
    const hasChangeControlStatus = stepData.changeControlStatuses?.some(status => status.valueBms === true || status.valueCmo === true);
    if (!hasChangeControlStatus) {
      validationErrors.push("At least one Change Control Status must be selected (BMS or CMO)");
    }
    
    const hasDeviationStatus = stepData.deviationStatuses?.some(status => status.valueBms === true || status.valueCmo === true);
    if (!hasDeviationStatus) {
      validationErrors.push("At least one Deviation Status must be selected (BMS or CMO)");
    }
    
    const hasInProcessTestStatus = stepData.inProcessTestStatuses?.some(status => status.valueBms === true || status.valueCmo === true);
    if (!hasInProcessTestStatus) {
      validationErrors.push("At least one In-Process Test Status must be selected (BMS or CMO)");
    }
    
    const hasProductReleaseTestStatus = stepData.productReleaseTestStatuses?.some(status => status.valueBms === true || status.valueCmo === true);
    if (!hasProductReleaseTestStatus) {
      validationErrors.push("At least one Product Release Test Status must be selected (BMS or CMO)");
    }
    
    // If there are validation errors, show them and stop
    if (validationErrors.length > 0) {
      setApiError(validationErrors.join(". "));
      setIsSaving(false);
      return;
    }
    
    try {
      // Get required IDs
      const collabrixProjectID = getCollabrixProjectID();
      const cdmoOrgID = getCdmoOrgID();
      
      console.log("🔍 StatusSelection - Retrieved IDs:", {
        collabrixProjectID,
        cdmoOrgID
      });

      // Map all status types to API format
      const allStatuses = [
        ...mapStatusesToAPI(stepData.projectStatuses || [], "project", false),
        ...mapStatusesToAPI(stepData.campaignStatuses || [], "campaign", false),
        ...mapStatusesToAPI(stepData.changeControlStatuses || [], "changeControl", true),
        ...mapStatusesToAPI(stepData.deviationStatuses || [], "deviation", true),
        ...mapStatusesToAPI(stepData.inProcessTestStatuses || [], "inProcessTest", true),
        ...mapStatusesToAPI(stepData.productReleaseTestStatuses || [], "productReleaseTest", true),
      ];

      // Build payload for updateProjectStatuses API
      const payload = {
        collabrixProjectID: collabrixProjectID,
        cdmoOrgID: cdmoOrgID,
        statuses: allStatuses,
      };

      console.log("=== updateProjectStatuses API Payload ===");
      console.log(JSON.stringify(payload, null, 2));
      console.log("=== END PAYLOAD ===");

      // Call API
      const response = await updateProjectStatusesAPI(payload);
      
      if (response && response.status) {
        console.log("✅ updateProjectStatuses API Response:", response);
        
        // Ensure latest data is saved to session storage
        if (typeof updateStepData === "function") {
          updateStepData("fgStatuses", stepData);
        }

        // Only navigate on success
        if (markApiCallSuccessful) {
          markApiCallSuccessful(true, null, "Status Selection");
        } else {
          handleNext();
        }
      } else {
        throw new Error(response?.message || "Failed to update project statuses");
      }
    } catch (error) {
      console.error("Error saving status selection:", error);
      // Show error message and do NOT navigate
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to save status selection. Please try again.";
      setApiError(errorMessage);
      setIsSaving(false);
    }
  }, [stepData, updateStepData, markApiCallSuccessful, handleNext, getCollabrixProjectID, getCdmoOrgID, mapStatusesToAPI]);

  // Helper function to render each status type table - matching ConfigProductDetails layout
  const renderStatusTable = (title, statuses, category, hasBmsCmo = false) => (
    <TableContainer style={{ maxWidth: "100%" }}>
      <Table size="small" style={{ width: "auto", minWidth: "300px" }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ fontWeight: "bold" }}>{title}</TableCell>
            {hasBmsCmo ? (
              <>
                <TableCell style={{ fontWeight: "bold" }} align="right">
                  BMS Selection
                </TableCell>
                <TableCell style={{ fontWeight: "bold" }} align="right">
                  CMO Selection
                </TableCell>
              </>
            ) : (
              <TableCell style={{ fontWeight: "bold" }} align="right">
                Selection
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.isArray(statuses) && statuses?.length > 0 ? (
            statuses.map((status, index) => {
              // Unique, stable key: status_id can repeat within a category (the
              // API/merge can return duplicates), so scope by category + index.
              const rowKey = `${category}-${status?.status_id ?? "na"}-${index}`;
              if (hasBmsCmo) {
                const isCheckedBms = status?.valueBms || false;
                const isCheckedCmo = status?.valueCmo || false;
                
                return (
                  <TableRow key={rowKey}>
                    <TableCell>{status?.status_name || "Unknown"}</TableCell>
                    <TableCell align="right">
                      <span style={{ position: "relative" }}>
                        <IOSSwitch
                          className="ios-switch"
                          disabled={readOnly}
                          checked={isCheckedBms}
                          onChange={(e) =>
                            handleToggleStatus(
                              category,
                              status?.status_id,
                              e.target.checked,
                              "bms"
                            )
                          }
                          color="primary"
                        />
                        {isCheckedBms && <Check className="checked-switch" />}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={{ position: "relative" }}>
                        <IOSSwitch
                          className="ios-switch"
                          disabled={readOnly}
                          checked={isCheckedCmo}
                          onChange={(e) =>
                            handleToggleStatus(
                              category,
                              status?.status_id,
                              e.target.checked,
                              "cmo"
                            )
                          }
                          color="primary"
                        />
                        {isCheckedCmo && <Check className="checked-switch" />}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              } else {
                const isChecked = status?.value || false;
                
                return (
                  <TableRow key={rowKey}>
                    <TableCell>{status?.status_name || "Unknown"}</TableCell>
                    <TableCell align="right">
                      <span style={{ position: "relative" }}>
                        <IOSSwitch
                          className="ios-switch"
                          disabled={readOnly}
                          checked={isChecked}
                          onChange={(e) =>
                            handleToggleStatus(
                              category,
                              status?.status_id,
                              e.target.checked
                            )
                          }
                          color="primary"
                        />
                        {isChecked && <Check className="checked-switch" />}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              }
            })
          ) : (
            <TableRow className="no-status-row">
              <TableCell colSpan={hasBmsCmo ? 3 : 2} align="center">
                {isLoading ? "Loading..." : "No statuses are available"}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {/* Loading indicator */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" my={3}>
          <CircularProgress />
        </Box>
      )}

      {/* Error message */}
      {error && !isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" my={3}>
          <Typography variant="body2" style={{ color: "red" }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* Status tables - matching screen design */}
      {!isLoading && !error && (
        <>
          <Grid container spacing={3} className="stepper-table">
            {/* Project Status Selection */}
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h6" 
                className={parentClasses?.formTitle} 
                style={{ 
                  marginBottom: "16px",
                }}
              >
                Project Status Selection
              </Typography>
              <Paper elevation={0}>
                {renderStatusTable("Project Status", stepData.projectStatuses, "projectstatus")}
              </Paper>
            </Grid>

            {/* Campaign Status Selection */}
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h6" 
                className={parentClasses?.formTitle} 
                style={{ 
                  marginBottom: "16px",
                }}
              >
                Campaign Status Selection
              </Typography>
              <Paper elevation={0}>
                {renderStatusTable("Campaign Status", stepData.campaignStatuses, "campaignstatus")}
              </Paper>
            </Grid>

            {/* Change Control Status Selection */}
            <Grid item xs={12} md={6} style={{ marginTop: "24px" }}>
              <Typography 
                variant="h6" 
                className={parentClasses?.formTitle} 
                style={{ 
                  marginBottom: "16px",
                }}
              >
                Change Control Status Selection
              </Typography>
              <Paper elevation={0}>
                {renderStatusTable("Change Control Status", stepData.changeControlStatuses, "changecontrolstatus", true)}
              </Paper>
            </Grid>

            {/* Deviation Status Selection */}
            <Grid item xs={12} md={6} style={{ marginTop: "24px" }}>
              <Typography 
                variant="h6" 
                className={parentClasses?.formTitle} 
                style={{ 
                  marginBottom: "16px",
                }}
              >
                Deviation Status Selection
              </Typography>
              <Paper elevation={0}>
                {renderStatusTable("Deviation Status", stepData.deviationStatuses, "deviationstatus", true)}
              </Paper>
            </Grid>

            {/* In-Process Test Status Selection */}
            <Grid item xs={12} md={6} style={{ marginTop: "24px" }}>
              <Typography 
                variant="h6" 
                className={parentClasses?.formTitle} 
                style={{ 
                  marginBottom: "16px",
                }}
              >
                In-Process Test Status Selection
              </Typography>
              <Paper elevation={0}>
                {renderStatusTable("In-Process Test Status", stepData.inProcessTestStatuses, "inprocessteststatus", true)}
              </Paper>
            </Grid>

            {/* Product Release Test Status Selection */}
            <Grid item xs={12} md={6} style={{ marginTop: "24px" }}>
              <Typography 
                variant="h6" 
                className={parentClasses?.formTitle} 
                style={{ 
                  marginBottom: "16px",
                }}
              >
                Product Release Test Status Selection
              </Typography>
              <Paper elevation={0}>
                {renderStatusTable("Product Release Test Status", stepData.productReleaseTestStatuses, "productreleaseteststatus", true)}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* API Error Message */}
      {apiError && (
        <Box
          style={{
            marginTop: "24px",
            padding: "12px 16px",
            backgroundColor: "#ffebee",
            border: "1px solid #f44336",
            borderRadius: "4px",
            marginBottom: "16px",
          }}
        >
          <Typography style={{ color: "#d32f2f", fontSize: "14px", fontWeight: 500 }}>
            {apiError}
          </Typography>
        </Box>
      )}

      {/* Navigation Buttons - Hidden when used in review page */}
      {!hideStepActions && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "80px",
            paddingTop: "24px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            variant="contained"
            onClick={handleBack}
            className={parentClasses?.button}
            style={{
              backgroundColor: "#9E9E9E",
              color: "#fff",
              padding: "10px 24px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              borderRadius: 30,
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAndNext}
            disabled={isSaving}
            className={parentClasses?.button}
            style={{
              backgroundColor: "#ec6535",
              color: "#fff",
              padding: "10px 32px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              boxShadow: "0 2px 4px rgba(236, 101, 53, 0.3)",
              borderRadius: 30,
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8, color: "#fff" }} />
                Saving...
              </>
            ) : (
              "Save & Next"
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default StatusSelection;

